/**
 * Shrink strategy implementations.
 */

import { ShrinkStrategy, ShrinkType } from "./types";

const EMPTY_U32 = new Uint32Array(0);

// ============================================================================
// None Strategy - No compression, just wrap the array
// ============================================================================

const noneStrategy: ShrinkStrategy = {
    shrink: (ids) => ({
        type: ShrinkType.None,
        data: ids as number[] | Uint32Array,
        length: ids.length,
    }),

    expand: (shrunk) => {
        if (shrunk.length === 0) {
            return EMPTY_U32;
        }

        if (shrunk.data instanceof Uint32Array) {
            return shrunk.data;
        }
        
        return Uint32Array.from(shrunk.data as number[]);
    },

    estimateSize: (ids) => ids.length * 8, // 8 bytes per number in JS
};

// ============================================================================
// Bitmap Strategy - 1 bit per potential ID in range
// ============================================================================

const bitmapStrategy: ShrinkStrategy = {
    shrink: (ids) => {
        if (ids.length === 0) {
            return { type: ShrinkType.Bitmap, data: new Uint8Array(0), minId: 0, length: 0 };
        }

        const minId = ids[0];
        const maxId = ids[ids.length - 1];
        const range = maxId - minId + 1;
        const byteLength = Math.ceil(range / 8);
        const bitmap = new Uint8Array(byteLength);

        for (const id of ids) {
            const offset = id - minId;
            const byteIndex = Math.floor(offset / 8);
            const bitIndex = offset % 8;
            bitmap[byteIndex] |= 1 << bitIndex;
        }

        return {
            type: ShrinkType.Bitmap,
            data: bitmap,
            minId,
            length: ids.length,
        };
    },

    expand: (shrunk) => {
        const bitmap = shrunk.data as Uint8Array;
        const minId = shrunk.minId ?? 0;
        const result = new Uint32Array(shrunk.length);
        let idx = 0;

        for (let byteIndex = 0; byteIndex < bitmap.length; byteIndex++) {
            const byte = bitmap[byteIndex];
            if (byte === 0) {
                continue;
            }

            const baseId = minId + byteIndex * 8;
            for (let bitIndex = 0; bitIndex < 8; bitIndex++) {
                if (byte & (1 << bitIndex)) {
                    result[idx++] = baseId + bitIndex;
                }
            }
        }

        return result;
    },

    estimateSize: (_ids, minId, maxId) => {
        const range = maxId - minId + 1;
        return Math.ceil(range / 8); // 1 bit per ID in range
    },
};

// ============================================================================
// Bitmap + RLE Strategy - Run-length encoded bitmap
// ============================================================================

const bitmapRLEStrategy: ShrinkStrategy = {
    shrink: (ids) => {
        if (ids.length === 0) {
            return { type: ShrinkType.BitmapRLE, data: new Uint8Array(0), minId: 0, length: 0 };
        }

        // First create the bitmap
        const bitmapShrunk = bitmapStrategy.shrink(ids);
        const bitmap = bitmapShrunk.data as Uint8Array;

        // Then RLE encode the bitmap
        const rleData: number[] = [];
        let i = 0;

        while (i < bitmap.length) {
            const value = bitmap[i];
            let count = 1;

            // Count consecutive identical bytes (max 255)
            while (i + count < bitmap.length && bitmap[i + count] === value && count < 255) {
                count++;
            }

            rleData.push(count, value);
            i += count;
        }

        return {
            type: ShrinkType.BitmapRLE,
            data: new Uint8Array(rleData),
            minId: bitmapShrunk.minId,
            length: ids.length,
        };
    },

    expand: (shrunk) => {
        const rleData = shrunk.data as Uint8Array;
        const minId = shrunk.minId ?? 0;
        const result = new Uint32Array(shrunk.length);
        let idx = 0;
        let byteOffset = 0;

        for (let i = 0; i < rleData.length; i += 2) {
            const count = rleData[i];
            const value = rleData[i + 1];

            // Skip zero runs in O(1) - major optimization for sparse data
            if (value === 0) {
                byteOffset += count;
                continue;
            }

            // Expand this run of non-zero bytes
            for (let j = 0; j < count; j++) {
                const baseId = minId + byteOffset * 8;
                for (let bitIndex = 0; bitIndex < 8; bitIndex++) {
                    if (value & (1 << bitIndex)) {
                        result[idx++] = baseId + bitIndex;
                    }
                }
                byteOffset++;
            }
        }

        return result;
    },

    estimateSize: (ids, _minId, _maxId) => {
        if (ids.length === 0) {
            return 0;
        }

        const range = ids[ids.length - 1] - ids[0] + 1;
        const bitmapBytes = Math.ceil(range / 8);
        const density = ids.length / range;

        if (density > 0.875) {
            // Dense data: most bytes are 0xFF, forms long runs
            // Estimate: ceil(bitmapBytes / 255) runs of 0xFF
            return Math.ceil(bitmapBytes / 255) * 2;
        }

        // Sparse data: estimate based on byte transitions
        // Count distinct byte indices touched
        let prevByteIndex = -1;
        let distinctBytes = 0;
        let gapBytes = 0;

        for (const id of ids) {
            const byteIndex = Math.floor((id - ids[0]) / 8);
            if (byteIndex !== prevByteIndex) {
                if (prevByteIndex >= 0) {
                    gapBytes += byteIndex - prevByteIndex - 1;
                }
                distinctBytes++;
                prevByteIndex = byteIndex;
            }
        }

        // Non-zero bytes likely have different values (don't compress well)
        // Zero gaps compress well into runs
        const zeroRuns = gapBytes > 0 ? Math.ceil(gapBytes / 255) : 0;
        const nonZeroRuns = distinctBytes;

        return (zeroRuns + nonZeroRuns) * 2;
    },
};

// ============================================================================
// Chunked Bitmap Strategy - Multiple bitmaps with offsets for gappy data
// ============================================================================

/** Gap threshold in IDs - if gap > this, start a new chunk */
const CHUNK_GAP_THRESHOLD = 1024;

/** Per-chunk overhead: 4 bytes offset + 2 bytes length */
const CHUNK_OVERHEAD = 8;

const chunkedBitmapStrategy: ShrinkStrategy = {
    shrink: (ids) => {
        if (ids.length === 0) {
            return { type: ShrinkType.ChunkedBitmap, data: new Uint8Array(0), length: 0 };
        }

        // Split IDs into chunks based on gaps
        const chunks: { startId: number; ids: number[] }[] = [];
        let currentChunk: { startId: number; ids: number[] } = { startId: ids[0], ids: [] };

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const prevId = i > 0 ? ids[i - 1] : id;
            const gap = id - prevId;

            if (gap > CHUNK_GAP_THRESHOLD && currentChunk.ids.length > 0) {
                // Gap too large, finalize current chunk and start new one
                chunks.push(currentChunk);
                currentChunk = { startId: id, ids: [] };
            }

            currentChunk.ids.push(id);
        }

        // Don't forget the last chunk
        if (currentChunk.ids.length > 0) {
            chunks.push(currentChunk);
        }

        // Build each chunk's bitmap
        const chunkBitmaps: { offset: number; bitmap: Uint8Array }[] = [];
        for (const chunk of chunks) {
            const minId = chunk.ids[0];
            const maxId = chunk.ids[chunk.ids.length - 1];
            const range = maxId - minId + 1;
            const byteLength = Math.ceil(range / 8);
            const bitmap = new Uint8Array(byteLength);

            for (const id of chunk.ids) {
                const offset = id - minId;
                const byteIndex = Math.floor(offset / 8);
                const bitIndex = offset % 8;
                bitmap[byteIndex] |= 1 << bitIndex;
            }

            chunkBitmaps.push({ offset: minId, bitmap });
        }

        // Serialize: [numChunks (2 bytes)] + [offset (4) + len (2) + bitmap]...
        let totalSize = 2; // numChunks header
        for (const { bitmap } of chunkBitmaps) {
            totalSize += CHUNK_OVERHEAD + bitmap.length;
        }

        const data = new Uint8Array(totalSize);
        const view = new DataView(data.buffer);

        // Write number of chunks
        view.setUint16(0, chunkBitmaps.length, true);

        let pos = 2;
        for (const { offset, bitmap } of chunkBitmaps) {
            view.setUint32(pos, offset, true);
            pos += 4;
            view.setUint32(pos, bitmap.length, true);
            pos += 4;
            data.set(bitmap, pos);
            pos += bitmap.length;
        }

        return {
            type: ShrinkType.ChunkedBitmap,
            data,
            length: ids.length,
        };
    },

    expand: (shrunk) => {
        const data = shrunk.data as Uint8Array;
        if (data.length === 0) {
            return EMPTY_U32;
        }

        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        const result = new Uint32Array(shrunk.length);
        let idx = 0;

        const numChunks = view.getUint16(0, true);
        let pos = 2;

        for (let c = 0; c < numChunks; c++) {
            const offset = view.getUint32(pos, true);
            pos += 4;
            const bitmapLen = view.getUint32(pos, true);
            pos += 4;

            // Expand this chunk's bitmap
            for (let byteIndex = 0; byteIndex < bitmapLen; byteIndex++) {
                const byte = data[pos + byteIndex];
                if (byte === 0) {
                    continue;
                }

                const baseId = offset + byteIndex * 8;
                for (let bitIndex = 0; bitIndex < 8; bitIndex++) {
                    if (byte & (1 << bitIndex)) {
                        result[idx++] = baseId + bitIndex;
                    }
                }
            }

            pos += bitmapLen;
        }

        return result;
    },

    estimateSize: (ids, _minId, _maxId) => {
        if (ids.length === 0) {
            return 0;
        }

        // Count chunks and their sizes
        let numChunks = 1;
        let totalBitmapBytes = 0;
        let chunkStart = ids[0];

        for (let i = 1; i < ids.length; i++) {
            const gap = ids[i] - ids[i - 1];
            if (gap > CHUNK_GAP_THRESHOLD) {
                // Finalize previous chunk
                const chunkEnd = ids[i - 1];
                totalBitmapBytes += Math.ceil((chunkEnd - chunkStart + 1) / 8);
                numChunks++;
                chunkStart = ids[i];
            }
        }

        // Last chunk
        const chunkEnd = ids[ids.length - 1];
        totalBitmapBytes += Math.ceil((chunkEnd - chunkStart + 1) / 8);

        // Total: 2 bytes header + (6 bytes overhead + bitmap) per chunk
        return 2 + numChunks * CHUNK_OVERHEAD + totalBitmapBytes;
    },
};

// ============================================================================
// Strategy Registry
// ============================================================================

export const strategies: Record<ShrinkType, ShrinkStrategy> = {
    [ShrinkType.None]: noneStrategy,
    [ShrinkType.Bitmap]: bitmapStrategy,
    [ShrinkType.BitmapRLE]: bitmapRLEStrategy,
    [ShrinkType.ChunkedBitmap]: chunkedBitmapStrategy,
};
